<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- Hero - oversized lock_outline watermark behind the headline.
                 Watermark structure is the shared mkt-hero-watermark pattern;
                 each marketing hero picks a thematic outlined icon. -->
            <section class="mkt-hero-light mkt-hero-watermark" style="padding:80px 0 40px;">
                <div class="mkt-hero-arcs" aria-hidden="true">
                    <svg class="mkt-hero-arc mkt-hero-arc--bl" viewBox="0 0 600 400" preserveAspectRatio="xMinYMax slice">
                        <defs>
                            <linearGradient id="arcGradBL" x1="0%" y1="100%" x2="100%" y2="0%">
                                <stop offset="0%" stop-color="#4F46E5" stop-opacity="0" />
                                <stop offset="38%" stop-color="#4F46E5" stop-opacity="1" />
                                <stop offset="100%" stop-color="#7C3AED" stop-opacity="0" />
                            </linearGradient>
                        </defs>
                        <g stroke="url(#arcGradBL)" stroke-width="0.7" fill="none">
                            <path d="M -50,350 Q 150,220 380,200 T 700,170" />
                            <path d="M -50,358 Q 155,230 385,210 T 700,180" />
                            <path d="M -50,366 Q 160,240 390,220 T 700,190" />
                            <path d="M -50,374 Q 165,250 395,230 T 700,200" />
                            <path d="M -50,382 Q 170,260 400,240 T 700,210" />
                            <path d="M -50,390 Q 175,270 405,250 T 700,220" />
                            <path d="M -50,398 Q 180,280 410,260 T 700,230" />
                            <path d="M -50,406 Q 185,290 415,270 T 700,240" />
                            <path d="M -50,414 Q 190,300 420,280 T 700,250" />
                            <path d="M -50,422 Q 195,310 425,290 T 700,260" />
                            <path d="M -50,430 Q 200,320 430,300 T 700,270" />
                            <path d="M -50,438 Q 205,330 435,310 T 700,280" />
                        </g>
                    </svg>
                    <svg class="mkt-hero-arc mkt-hero-arc--tr" viewBox="0 0 600 400" preserveAspectRatio="xMinYMax slice">
                        <defs>
                            <linearGradient id="arcGradTR" x1="0%" y1="100%" x2="100%" y2="0%">
                                <stop offset="0%" stop-color="#0EA5E9" stop-opacity="0" />
                                <stop offset="38%" stop-color="#0EA5E9" stop-opacity="1" />
                                <stop offset="100%" stop-color="#4F46E5" stop-opacity="0" />
                            </linearGradient>
                        </defs>
                        <g stroke="url(#arcGradTR)" stroke-width="0.7" fill="none">
                            <path d="M -50,350 Q 150,220 380,200 T 700,170" />
                            <path d="M -50,358 Q 155,230 385,210 T 700,180" />
                            <path d="M -50,366 Q 160,240 390,220 T 700,190" />
                            <path d="M -50,374 Q 165,250 395,230 T 700,200" />
                            <path d="M -50,382 Q 170,260 400,240 T 700,210" />
                            <path d="M -50,390 Q 175,270 405,250 T 700,220" />
                            <path d="M -50,398 Q 180,280 410,260 T 700,230" />
                            <path d="M -50,406 Q 185,290 415,270 T 700,240" />
                            <path d="M -50,414 Q 190,300 420,280 T 700,250" />
                            <path d="M -50,422 Q 195,310 425,290 T 700,260" />
                            <path d="M -50,430 Q 200,320 430,300 T 700,270" />
                            <path d="M -50,438 Q 205,330 435,310 T 700,280" />
                        </g>
                    </svg>
                </div>
                <div class="mkt-hero-watermark-icon mkt-hero-watermark-icon--right" aria-hidden="true">
                    <q-icon name="lock_outline" />
                </div>
                <div class="mkt-container text-center" style="position:relative;z-index:1;">
                    <h1 class="text-weight-bold q-mb-sm" style="font-size:clamp(2rem,4vw,3rem);line-height:1.15;color:#0F172A;">
                        Your documents deserve<br><span class="text-mkt-indigo">bank-level security</span>
                    </h1>
                    <p class="q-mb-lg" style="font-size:1.1rem;max-width:600px;margin:0 auto;color:#475569;">
                        Every document is encrypted, every action is logged, and every signer is verified.
                        Security is built into every layer of the platform.
                    </p>
                    <!-- Trust pills -->
                    <div class="row justify-center items-center mkt-hero-pills-row" style="flex-wrap:wrap;gap:12px;">
                        <span class="mkt-trust-pill mkt-trust-pill--light"><q-icon name="verified" class="mkt-trust-icon" /> Hash-chained audit trail</span>
                        <span class="mkt-trust-pill mkt-trust-pill--light"><q-icon name="approval" class="mkt-trust-icon" /> PKI-signed PDFs with TSA</span>
                        <span class="mkt-trust-pill mkt-trust-pill--light"><q-icon name="lock" class="mkt-trust-icon" /> AES-256 at rest</span>
                        <span class="mkt-trust-pill mkt-trust-pill--light"><q-icon name="gavel" class="mkt-trust-icon" /> ESIGN Act & UETA</span>
                        <span class="mkt-trust-pill mkt-trust-pill--light"><q-icon name="cloud" class="mkt-trust-icon" /> Azure Infrastructure</span>
                    </div>
                </div>
            </section>

            <!-- Soft curve divider: hero → security sections -->
            <svg class="mkt-divider-soft" viewBox="0 0 1440 56" preserveAspectRatio="none">
                <path d="M0,0 C320,48 720,56 1080,32 C1260,20 1360,24 1440,16 L1440,56 L0,56 Z" fill="#ffffff" />
            </svg>

            <!-- ============================================================
                 SECURITY BLOCKS - one ordered loop interleaves the fake-UI
                 mockup sections, the branded deep-dive spotlights, and the
                 dark partner-logos band so the page never runs five identical
                 2-column sections in a row. The auth + audit-cert spotlights
                 REPLACE the former Signer-Authentication and Audit-Trail
                 mockup sections (same content, higher-impact branded art);
                 the dark partner band sits mid-body as a tonal break. Zebra
                 striping is computed across mockup-section blocks only.
                 ============================================================ -->
            <template v-for="block in securityBlocks" :key="block.k">
                <!-- SECTION: 2-column fake-UI mockup, dispatched by mockType -->
                <section v-if="block.type === 'section'"
                    v-intersection="revealCfg"
                    :class="[block.bg, block.reveal]" style="padding:60px 0;">
                    <div class="mkt-container">
                        <div class="row q-col-gutter-xl items-center" :class="block.reverse ? 'reverse' : ''">
                            <div class="col-12 col-md-6">
                                <q-item class="q-pa-none q-mb-md">
                                    <q-item-section avatar style="min-width:72px;">
                                        <div class="mkt-icon-circle" :class="block.sec.iconClass">
                                            <q-icon :name="block.sec.icon" />
                                        </div>
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.5rem;color:#0a0f1a;line-height:1.2;">{{ block.sec.title }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <p class="text-grey-7 q-mb-lg" style="font-size:1rem;line-height:1.7;">{{ block.sec.description }}</p>
                                <q-list dense class="mkt-feature-list">
                                    <q-item v-for="item in block.sec.items" :key="item.label" class="q-px-none">
                                        <q-item-section avatar top style="min-width:28px;padding-right:0;">
                                            <q-icon name="check_circle" color="positive" size="20px" />
                                        </q-item-section>
                                        <q-item-section>
                                            <q-item-label style="font-size:0.9rem;line-height:1.55;">
                                                <span class="text-weight-bold" style="color:#0a0f1a;">{{ item.label }}</span>
                                                <span v-if="item.detail" class="text-grey-7" style="font-size:0.85rem;"> - {{ item.detail }}</span>
                                            </q-item-label>
                                        </q-item-section>
                                    </q-item>
                                </q-list>
                            </div>
                            <div class="col-12 col-md-6">
                                <div class="mkt-mockup-frame">
                                    <div class="mkt-mockup-chrome">
                                        <span class="mkt-mockup-dot" style="background:#FF5F57;"></span>
                                        <span class="mkt-mockup-dot" style="background:#FFBD2E;"></span>
                                        <span class="mkt-mockup-dot" style="background:#28CA42;"></span>
                                        <span class="mkt-mockup-url">{{ block.sec.mockUrl }}</span>
                                    </div>

                                    <!-- ENCRYPTION -->
                                    <div v-if="block.sec.mockType === 'encryption'" class="mkt-mockup-body">
                                        <div class="row items-center q-gutter-sm q-mb-md">
                                            <q-icon name="lock" size="22px" color="positive" />
                                            <div class="text-weight-bold" style="font-size:0.9rem;color:#0a0f1a;">Encrypted at rest</div>
                                            <q-space />
                                            <q-badge color="positive" label="AES-256" />
                                        </div>
                                        <div class="mkt-mockup-cipher q-mb-md">
                                            <div v-for="i in 5" :key="i" class="mkt-mockup-cipher-row">
                                                <span v-for="j in 28" :key="j" class="mkt-mockup-cipher-bit" :style="`opacity:${0.35 + Math.random() * 0.5};`"></span>
                                            </div>
                                        </div>
                                        <div class="row q-gutter-xs" style="flex-wrap:wrap;">
                                            <q-chip dense color="indigo-1" text-color="indigo-9" label="Encrypted in transit" size="sm" />
                                            <q-chip dense color="green-1" text-color="green-9" label="Encrypted database" size="sm" />
                                            <q-chip dense color="amber-1" text-color="amber-9" label="Encrypted storage" size="sm" />
                                        </div>
                                    </div>

                                    <!-- INFRASTRUCTURE -->
                                    <div v-else-if="block.sec.mockType === 'infra'" class="mkt-mockup-body">
                                        <div class="row items-center q-gutter-sm q-mb-md">
                                            <q-icon name="cloud" size="22px" color="primary" />
                                            <div class="text-weight-bold" style="font-size:0.9rem;color:#0a0f1a;">Azure regions</div>
                                            <q-space />
                                            <span style="font-size:0.7rem;color:#64748B;">99.9% uptime target</span>
                                        </div>
                                        <div class="row q-gutter-sm" style="flex-wrap:wrap;">
                                            <div v-for="r in mockRegions" :key="r.name" class="mkt-mockup-region">
                                                <q-icon name="circle" size="8px" :color="r.healthy ? 'positive' : 'grey-5'" />
                                                <span style="font-size:0.78rem;color:#0a0f1a;font-weight:500;">{{ r.name }}</span>
                                            </div>
                                        </div>
                                        <div class="mkt-mockup-uptime q-mt-md">
                                            <div v-for="i in 30" :key="i" class="mkt-mockup-uptime-bar" :style="`background:${i === 12 ? '#B45309' : '#047857'};`"></div>
                                        </div>
                                        <div class="row justify-between q-mt-xs" style="font-size:0.65rem;color:#64748B;">
                                            <span>30 days ago</span>
                                            <span>Today</span>
                                        </div>
                                    </div>

                                    <!-- DOCUMENT INTEGRITY (PKI seal) -->
                                    <div v-else-if="block.sec.mockType === 'pkiseal'" class="mkt-mockup-body">
                                        <div class="text-weight-bold q-mb-md" style="font-size:0.9rem;color:#0a0f1a;">Signature panel - Adobe Reader</div>
                                        <div style="border:1px solid #E2E8F0;border-radius:8px;padding:14px 16px;background:#F8FAFC;">
                                            <div class="row items-center q-gutter-sm q-mb-sm">
                                                <q-icon name="check_circle" color="positive" size="20px" />
                                                <div class="text-weight-bold" style="font-size:0.85rem;color:#047857;">Signed and all signatures are valid</div>
                                            </div>
                                            <div style="font-size:0.72rem;color:#475569;line-height:1.6;">
                                                <div>Signer: eSign Launchpad Platform</div>
                                                <div>Reason: Document integrity seal</div>
                                                <div>Time: 2026-04-26 14:32:18 -05:00 <span style="color:#047857;">(verified by DigiCert TSA)</span></div>
                                                <div>LTV enabled · revocation data embedded</div>
                                            </div>
                                        </div>
                                        <div class="row items-center q-gutter-xs q-mt-md">
                                            <q-icon name="lock" color="positive" size="14px" />
                                            <span style="font-size:0.7rem;color:#64748B;">PKCS#7 signature embedded - any post-signing edit invalidates this seal</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- SPOTLIGHT: branded deep-dive band (props supplied by securityBlocks) -->
                <marketing-spotlight v-else-if="block.type === 'spotlight'" v-bind="block.spot" />

                <!-- PARTNERS: dark infrastructure-partner band - the page's single
                     tonal break, placed mid-body between the two spotlights. -->
                <section v-else-if="block.type === 'partners'" class="mkt-dark reveal-fade-in" v-intersection="revealCfg" style="padding:60px 0;">
                    <div class="mkt-container text-center">
                        <h2 class="text-white text-weight-bold q-mb-sm" style="font-size:1.6rem;">Built on trusted infrastructure</h2>
                        <p class="text-grey-5 q-mb-xl" style="font-size:1rem;">We partner with industry leaders for every layer of our security stack.</p>
                        <div class="row justify-center q-gutter-xl items-center">
                            <div v-for="partner in partners" :key="partner.name" class="text-center">
                                <div class="mkt-card--dark q-pa-lg" style="border-radius:12px;min-width:140px;">
                                    <q-icon :name="partner.icon" size="32px" class="text-mkt-indigo q-mb-sm" />
                                    <div class="text-white text-weight-bold" style="font-size:0.9rem;">{{ partner.name }}</div>
                                    <div class="text-grey-6" style="font-size:0.75rem;">{{ partner.role }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </template>

            <!-- Compliance details -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-xl">
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:1.8rem;color:#0a0f1a;">Legal Compliance</h2>
                        <p class="text-grey-7" style="font-size:1rem;">eSign Launchpad signatures are legally binding and court-admissible.</p>
                    </div>
                    <div class="row q-col-gutter-lg">
                        <div class="col-12 col-md-4" v-for="law in compliance" :key="law.name">
                            <div class="mkt-card q-pa-lg text-center" style="height:100%;">
                                <q-icon name="gavel" size="32px" class="text-mkt-gold q-mb-md" />
                                <h3 class="text-weight-bold q-mb-sm" style="font-size:1.05rem;color:#0a0f1a;">{{ law.name }}</h3>
                                <p class="text-grey-7" style="font-size:0.85rem;line-height:1.6;">{{ law.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- CTA -->
            <section class="mkt-cta-gradient reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container text-center">
                    <h2 class="text-white text-weight-bold q-mb-md" style="font-size:1.8rem;">Security questions?</h2>
                    <p class="text-white q-mb-lg" style="opacity:0.9;font-size:1rem;">We're happy to discuss our security practices in detail. Start a trial or contact our team.</p>
                    <div class="row justify-center q-gutter-md">
                        <q-btn color="white" text-color="primary" size="lg" label="Get Started Free" href="/Account/Register"
                            no-caps unelevated style="border-radius:10px;padding:10px 36px;font-weight:600;" />
                        <q-btn outline color="white" size="lg" label="Contact Sales" no-caps
                            style="border-radius:10px;padding:10px 36px;" />
                    </div>
                </div>
            </section>

            <!-- Footer -->
            <footer class="mkt-light" style="padding:40px 0 20px;border-top:1px solid #E2E8F0;">
                <div class="mkt-container">
                    <div class="row justify-between items-center">
                        <div class="row items-center">
                            <img src="/img/logo-nav.webp" alt="eSign Launchpad" width="82" height="22" style="height: 22px; width: auto;" />
                        </div>
                        <div class="row q-gutter-md">
                            <a href="/Home/Features" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Features</a>
                            <a href="/Home/Pricing" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Pricing</a>
                            <a href="/Home/SmallBusiness" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Small Business</a>
                            <a href="/news" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">News</a>
                            <a href="/privacy" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Privacy</a>
                            <a href="/terms" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Terms</a>
                        </div>
                        <div style="font-size:0.75rem;color:#64748B;">&copy; {{ currentYear }} eSign Launchpad</div>
                    </div>
                </div>
            </footer>
        </q-page-container>
    </q-layout>
</template>

<style scoped>
.reverse { flex-direction: row-reverse; }
</style>

<script>
import { logout } from '@services/authActions.js';
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingSpotlight from '@components/marketing/MarketingSpotlight.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'SecurityPage',
    mixins: [revealMixin],
    components: { MarketingNav, MarketingSpotlight },
    data() {
        return {
            isAuthenticated: typeof _AUTHENTICATED !== 'undefined' && _AUTHENTICATED === true,
            mobileNav: false,
            currentYear: new Date().getFullYear(),

            securitySections: [
                {
                    icon: 'lock', iconClass: 'mkt-icon-circle--green',
                    mockType: 'encryption', mockUrl: 'app.esignlaunchpad.com/security',
                    title: 'Encryption',
                    description: '256-bit encryption everywhere - files, database, and network. Bank-level cryptography from upload to download.',
                    items: [
                        { label: '256-bit encryption at rest', detail: 'Every stored document and record' },
                        { label: 'Encrypted in transit', detail: 'Modern protocols on every connection' },
                        { label: 'Encrypted document storage', detail: 'Server-side encryption on every file' },
                        { label: 'Encrypted database', detail: 'Per-customer data segregation throughout' }
                    ]
                },
                {
                    icon: 'cloud', iconClass: 'mkt-icon-circle--blue',
                    mockType: 'infra', mockUrl: 'status.esignlaunchpad.com',
                    title: 'Infrastructure',
                    description: 'Microsoft Azure infrastructure. Multi-region, multi-customer. The same enterprise cloud trusted by regulated industries.',
                    items: [
                        { label: 'Microsoft Azure cloud', detail: 'Trusted by Fortune 500' },
                        { label: 'Customer data isolation', detail: 'Strict per-account separation' },
                        { label: 'Edge protection + firewall', detail: 'DDoS shielding on every request' },
                        { label: 'Geo-redundant storage', detail: 'Replicated across regions' },
                        { label: '99.9% uptime target', detail: 'See our SLA in TOS' }
                    ]
                },
                {
                    icon: 'approval', iconClass: 'mkt-icon-circle--gold',
                    mockType: 'pkiseal', mockUrl: 'app.esignlaunchpad.com/evidence',
                    title: 'Document Integrity',
                    description: 'Every completed PDF is sealed with a PKI digital signature, a third-party trusted timestamp, and long-term validation data. Any post-signing modification is mathematically detectable.',
                    items: [
                        { label: 'PKI digital signature on every PDF', detail: 'CMS / PKCS#7 embedded in the document' },
                        { label: 'Third-party trusted timestamp', detail: 'DigiCert TSA proves when the document was sealed' },
                        { label: 'Long-Term Validation (LTV)', detail: 'Cert chain + revocation data embedded so signatures verify decades later' },
                        { label: 'Same protection for the Certificate of Completion', detail: 'CoC is itself signed and timestamped' },
                        { label: 'Tamper-evident in any PDF reader', detail: 'Adobe Reader, browser viewers, third-party tools all detect changes' }
                    ]
                }
            ],

            // Mock data for the Security page fake-UI previews. (mockRegions
            // drives the Infrastructure mockup; the former signerauth + audit
            // mockups - and their inline / mockHashBlocks data - were replaced
            // by the spot-auth + spot-audit-cert spotlights.)
            mockRegions: [
                { name: 'East US',      healthy: true },
                { name: 'West US 2',    healthy: true },
                { name: 'North Europe', healthy: true },
                { name: 'West Europe',  healthy: true },
                { name: 'SE Asia',      healthy: true }
            ],

            // Deep-dive spotlight configs consumed by securityBlocks via
            // v-bind. Keys are camelCase to match MarketingSpotlight props.
            // These REPLACE the former Signer-Authentication and Audit-Trail
            // mockup sections (same claims, branded art).
            spotlights: {
                auth: {
                    slug: 'spot-auth',
                    alt: 'Per-signer authentication options including government ID verification, knowledge-based quiz, and SMS challenge',
                    eyebrowIcon: 'fingerprint',
                    iconCircleClass: 'mkt-icon-circle--gold',
                    title: 'Prove they are who they say they are',
                    blurb: 'Per-signer authentication, configurable per recipient. From cheap SMS one-time codes through public-records knowledge quizzes to government-ID + selfie biometric matching.',
                    bullets: [
                        'SMS one-time password',
                        'Knowledge-based identity quiz (public records)',
                        'Government-ID verification with selfie biometrics',
                        'Configurable per signer, logged in the audit trail'
                    ],
                    flip: true
                },
                auditCert: {
                    slug: 'spot-audit-cert',
                    alt: 'Certificate of completion with SHA-256 hash chain and 30+ event types - tamper-evident audit trail',
                    eyebrowIcon: 'verified_user',
                    iconCircleClass: 'mkt-icon-circle--gold',
                    title: 'Evidence on every signing',
                    blurb: "Every completed package ships with a court-admissible Certificate of Completion: 30+ event types, SHA-256 hash chain, IP and UTC timestamp on every action, and a Platform Integrity Seal signed with a third-party trusted timestamp.",
                    bullets: [
                        'SHA-256 hash chain across every audit block',
                        '30+ event types from upload through download',
                        'PKI-signed PDF + DigiCert trusted timestamp',
                        'Evidence package ZIP, with 7-year retention on Business'
                    ]
                }
            },

            compliance: [
                {
                    name: 'ESIGN Act (U.S.)',
                    description: 'The Electronic Signatures in Global and National Commerce Act gives electronic signatures the same legal standing as handwritten signatures in the United States.'
                },
                {
                    name: 'UETA',
                    description: 'The Uniform Electronic Transactions Act, adopted by 49 U.S. states, establishes the legal equivalence of electronic records and signatures with paper documents.'
                },
                {
                    name: 'Azure Cloud Compliance',
                    description: 'We run on Microsoft Azure, whose infrastructure holds ISO 27001, SOC 2 Type II, HIPAA, and FedRAMP attestations. Our customer-facing controls build on that foundation while we pursue our own direct certifications.'
                }
            ],

            partners: [
                { icon: 'cloud', name: 'Microsoft Azure', role: 'Cloud Infrastructure' },
                { icon: 'payments', name: 'Stripe', role: 'Payment Processing' },
                { icon: 'badge', name: 'Persona', role: 'ID Verification' },
                { icon: 'quiz', name: 'Authenticate.com', role: 'KBA Provider' }
            ]
        };
    },
    computed: {
        // Ordered page body. One loop renders the mockup sections, the two
        // branded spotlights, and the dark partner band so they interleave
        // instead of running five identical 2-column sections in a row.
        // Order: encryption, infrastructure, [auth spotlight], DARK partners
        // (mid-body break), [audit-cert spotlight], document integrity. Zebra
        // striping (white/light + reverse) is computed across SECTION blocks
        // only, so the spotlights (wash) and partners (dark) don't disturb it.
        securityBlocks() {
            const sec = (mt) => this.securitySections.find((s) => s.mockType === mt);
            const raw = [
                { type: 'section', sec: sec('encryption') },
                { type: 'section', sec: sec('infra') },
                { type: 'spotlight', spot: this.spotlights.auth },
                { type: 'partners' },
                { type: 'spotlight', spot: this.spotlights.auditCert },
                { type: 'section', sec: sec('pkiseal') }
            ];
            let si = 0;
            return raw.map((b) => {
                if (b.type === 'section') {
                    const even = si % 2 === 0;
                    si += 1;
                    return {
                        ...b,
                        k: 'sec-' + b.sec.mockType,
                        bg: even ? 'mkt-white' : 'mkt-light',
                        reveal: even ? 'reveal-fade-up' : 'reveal-scale',
                        reverse: !even
                    };
                }
                if (b.type === 'spotlight') return { ...b, k: 'spot-' + b.spot.slug };
                return { ...b, k: 'partners' };
            });
        }
    },
    methods: { logout }
};
</script>
